import { axiosForReactQueryInstance } from '@shared/api/reactQuery';
import { hasSessionToken } from '@shared/api/session';
import { ServerLang } from '@shared/utils/i18n/locales';
import { type ApiResponse } from '@/shared/api/orval/models';

/**
 * ЯЗЫК ОБЩЕНИЯ ЧЕЛОВЕКА НА СЕРВЕРЕ.
 *
 * ═══ ПОЧЕМУ ЗДЕСЬ ТОЛЬКО ЗАПИСЬ, БЕЗ ПАРНОГО ЧТЕНИЯ ═══
 * Язык приезжает полем `lang` ответа `GET /api/Users` — того самого запроса, которым кабинет
 * узнаёт текущего пользователя (AuthServiceImpl.loadUserData). Отдельный GET означал бы второй
 * круг к серверу после входа и промежуток, в котором интерфейс уже нарисован, а язык ещё
 * не приехал, — то есть ВИДИМОЕ переключение языка на глазах у человека. Ради этого поле
 * и добавлено в существующий ответ, а не вынесено в свой маршрут.
 *
 * ═══ ПОЧЕМУ РУЧНОЙ ВЫЗОВ, А НЕ СГЕНЕРИРОВАННЫЙ ORVAL ═══
 * Генератор ходит за swagger'ом на тестовый контур (orval.config.ts -> 10.1.1.110:5656),
 * то есть сгенерировать клиент можно только после выкатки Core. Файл в @shared/api/ переживает
 * `yarn api:generate` (clean: true чистит только ./src/shared/api/orval), поэтому вызов лежит
 * здесь, а не в сгенерированном users.ts, откуда его стёрло бы первой же регенерацией.
 *
 * ═══ ЗАПИСЬ ФОНОВАЯ И МОЛЧАЛИВАЯ ═══
 * Переключение языка обязано срабатывать МГНОВЕННО: человек нажал «Қазақша» и видит казахский
 * немедленно, не дожидаясь сервера. Сервер догоняет. Ошибку сохранения кабинет человеку
 * не показывает (см. saveUserLang ниже) — красная плашка «не удалось сохранить язык» на каждом
 * подрагивании сети раздражала бы сильнее, чем польза от неё, а сам выбор при этом не теряется:
 * он остаётся в интерфейсе и в локальном кэше до следующего ответа сервера
 */
const putUserLang = (userId: number, lang: ServerLang) =>
  axiosForReactQueryInstance<ApiResponse>({
    url: `/api/Users/${userId}/lang`,
    method: 'PUT',
    data: { lang }
  });

/**
 * Сохранить язык человека на сервере. Возвращает true, если сервер принял.
 *
 * НЕ БРОСАЕТ НИКОГДА. Вызывающий — переключатель языка, и уронить его нечем: ни отсутствием
 * сессии, ни недоступным сервером, ни 403 из-за незаведённого маршрута в access_matrix.
 *
 * ПРОВЕРКА ТОКЕНА ОБЯЗАТЕЛЬНА И НЕ ЗАМЕНЯЕТСЯ ПРОВЕРКОЙ userId. Переключатель языка стоит
 * в том числе в шапке ЛЕНДИНГА, куда заходят неавторизованные; при этом id пользователя может
 * лежать в localStorage с прошлой сессии (persist AuthModule). Уйди запрос без токена — сервер
 * ответил бы 401, а response-интерцептор на 401 зовёт sessionExpired() и уводит человека
 * на страницу входа. То есть смена языка на лендинге выкидывала бы гостя с сайта
 */
export const saveUserLang = async (userId: number | null | undefined, lang: ServerLang): Promise<boolean> => {
  if (!userId || !hasSessionToken()) return false;

  try {
    // ПРОВЕРЯЕТСЯ КОД КОНВЕРТА, А НЕ ФАКТ РЕЗОЛВА. Core отдаёт бизнес-ошибки СО СТАТУСОМ 200
    // и code != 0 (см. response-интерцептор AXIOS_INSTANCE, который возвращает конверт целиком),
    // а на 401 интерцептор вообще резолвит промис значением undefined. То есть `await` без
    // проверки кода считал бы успехом и «пользователь не найден», и выброшенную сессию —
    // и кабинет запомнил бы в userData язык, которого на сервере нет
    const response = await putUserLang(userId, lang);
    return response?.code === 0;
  } catch {
    return false;
  }
};
